/**
 * AppShell.ts
 * Master Universal Layout Frame Component for ApnaGuru LMS & Story Animation Engine.
 * Features fixed top header with brand logo, Day/Night mode theme switcher,
 * fixed left sidebar navigation with Bootstrap Vector Icons, and isolated scrollable viewport.
 */
import { AuthService } from '../services/AuthService';
import { BrandLogo } from '../components/BrandLogo';

export class AppShell {
  private container: HTMLElement;
  private currentPath: string;
  private onNavigate: (path: string) => void;
  private currentTheme: 'dark' | 'light' = 'dark';

  constructor(container: HTMLElement, currentPath: string, onNavigate: (path: string) => void) {
    this.container = container;
    this.currentPath = currentPath;
    this.onNavigate = onNavigate;

    // Load saved theme preference
    const savedTheme = localStorage.getItem('apnaguru_theme') as 'dark' | 'light';
    if (savedTheme === 'light' || savedTheme === 'dark') {
      this.currentTheme = savedTheme;
    }
    document.body.setAttribute('data-theme', this.currentTheme);
  }

  public getViewportElement(): HTMLElement {
    return this.container.querySelector('#app-shell-viewport') as HTMLElement;
  }

  public render(): void {
    const auth = AuthService.getInstance();
    const user = auth.getUser();
    const canCreateCourse = auth.hasPermission('course_add') || auth.hasPermission('course.add') || auth.hasPermission('course.create');

    this.container.innerHTML = `
      <div class="app-shell-root" style="height: 100vh; max-height: 100vh; display: flex; flex-direction: column; overflow: hidden; background-color: var(--bg-app); color: var(--text-main);">
        
        <!-- Fixed Top Header Bar -->
        <header style="height: 64px; flex-shrink: 0; background: var(--bg-header); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border-color); display: flex; align-items: center; justify-content: space-between; padding: 0 28px; z-index: 1000; box-shadow: var(--shadow-card); box-sizing: border-border-box;">
          
          <div style="display: flex; align-items: center; gap: 20px;">
            <div id="app-shell-brand">
              ${BrandLogo.renderHTML('md')}
            </div>
          </div>

          <div style="display: flex; align-items: center; gap: 16px;">
            
            <!-- Day / Night Theme Switcher Button -->
            <button id="btn-toggle-theme" title="Toggle Day/Night Mode" style="background: var(--bg-surface); border: 1px solid var(--border-color); color: var(--brand-orange-primary); width: 38px; height: 38px; border-radius: 10px; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease;">
              <i class="bi ${this.currentTheme === 'dark' ? 'bi-sun-fill' : 'bi-moon-stars-fill'}"></i>
            </button>

            <!-- User Persona Badge -->
            <div style="text-align: right; display: flex; align-items: center; gap: 10px; background: var(--bg-surface); padding: 4px 12px; border-radius: 20px; border: 1px solid var(--border-color);">
              <i class="bi ${canCreateCourse ? 'bi-shield-check text-indigo-400' : 'bi-person-badge text-sky-400'}" style="font-size: 16px;"></i>
              <div style="text-align: right;">
                <div style="font-size: 13px; font-weight: 700; color: var(--text-main);">${user?.firstName || 'User'} ${user?.lastName || ''}</div>
                <div style="font-size: 10.5px; color: var(--brand-blue-primary); font-weight: 600;">${canCreateCourse ? 'Instructor / Creator' : 'Student / Learner'}</div>
              </div>
            </div>

            <!-- Logout Button -->
            <button id="btn-shell-logout" style="background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.25); color: #ef4444; padding: 8px 16px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background 0.2s;">
              <i class="bi bi-box-arrow-right"></i>
              <span>Logout</span>
            </button>

          </div>
        </header>

        <!-- Main Body Frame: Fixed Left Sidebar + Scrollable Viewport -->
        <div style="flex: 1; height: calc(100vh - 64px); display: flex; overflow: hidden;">
          
          <!-- Fixed Left Sidebar Navigation -->
          <aside style="width: 260px; min-width: 260px; height: 100%; background: var(--bg-sidebar); border-right: 1px solid var(--border-color); padding: 20px 14px; display: flex; flex-direction: column; justify-content: space-between; overflow-y: auto; flex-shrink: 0; box-sizing: border-box;">
            
            <div style="display: flex; flex-direction: column; gap: 24px;">
              
              <!-- Student / Learner Menu Group -->
              <div>
                <div style="font-size: 11px; font-weight: 800; color: var(--text-subtle); text-transform: uppercase; letter-spacing: 0.8px; padding: 0 12px 10px 12px; display: flex; align-items: center; gap: 6px;">
                  <i class="bi bi-mortarboard-fill" style="color: var(--brand-blue-primary);"></i>
                  <span>Student Hub</span>
                </div>
                <div style="display: flex; flex-direction: column; gap: 4px;">
                  <button class="shell-nav-btn ${this.isRouteActive('/lms-dashboard') ? 'active' : ''}" data-path="/lms-dashboard">
                    <i class="bi bi-grid-1x2-fill" style="color: var(--brand-blue-primary);"></i>
                    <span>Home Dashboard</span>
                  </button>
                  <button class="shell-nav-btn ${this.isRouteActive('/my-courses') ? 'active' : ''}" data-path="/my-courses">
                    <i class="bi bi-mortarboard-fill" style="color: var(--brand-blue-primary);"></i>
                    <span>My Courses</span>
                  </button>
                  <button class="shell-nav-btn ${this.isRouteActive('/my-exams') ? 'active' : ''}" data-path="/my-exams">
                    <i class="bi bi-patch-check-fill" style="color: #6366f1;"></i>
                    <span>My Exams</span>
                  </button>
                  <button class="shell-nav-btn ${this.isRouteActive('/my-assignments') ? 'active' : ''}" data-path="/my-assignments">
                    <i class="bi bi-laptop" style="color: #10b981;"></i>
                    <span>My Assignments</span>
                  </button>
                </div>
              </div>

              ${canCreateCourse ? `
                <!-- Instructor / Creator Menu Group -->
                <div>
                  <div style="font-size: 11px; font-weight: 800; color: var(--text-subtle); text-transform: uppercase; letter-spacing: 0.8px; padding: 0 12px 10px 12px; display: flex; align-items: center; gap: 6px;">
                    <i class="bi bi-shield-check" style="color: #6366f1;"></i>
                    <span>Instructor Studio</span>
                  </div>
                  <div style="display: flex; flex-direction: column; gap: 4px;">
                    <button class="shell-nav-btn ${this.isRouteActive('/courses') ? 'active' : ''}" data-path="/courses">
                      <i class="bi bi-journal-bookmark-fill" style="color: var(--brand-orange-primary);"></i>
                      <span>All Courses Catalog</span>
                    </button>
                    <button class="shell-nav-btn ${this.isRouteActive('/create-course') ? 'active' : ''}" data-path="/create-course">
                      <i class="bi bi-magic" style="color: var(--brand-orange-primary);"></i>
                      <span>Build Course with AI</span>
                    </button>
                    <button class="shell-nav-btn ${this.isRouteActive('/exam-generator') ? 'active' : ''}" data-path="/exam-generator">
                      <i class="bi bi-file-earmark-text-fill" style="color: #38bdf8;"></i>
                      <span>Apna ExamGuru</span>
                    </button>
                    <button class="shell-nav-btn ${this.isRouteActive('/assignment-generator') ? 'active' : ''}" data-path="/assignment-generator">
                      <i class="bi bi-clipboard2-check-fill" style="color: #c084fc;"></i>
                      <span>Apna AssignmentGuru</span>
                    </button>
                    <button class="shell-nav-btn ${this.isRouteActive('/assignment-checker') ? 'active' : ''}" data-path="/assignment-checker">
                      <i class="bi bi-check2-square" style="color: #fb923c;"></i>
                      <span>Assignment Submissions</span>
                    </button>
                  </div>
                </div>
              ` : ''}

            </div>

            <!-- Sidebar Footer -->
            <div style="padding-top: 16px; border-top: 1px solid var(--border-color); font-size: 11.5px; color: var(--text-subtle); text-align: center;">
              ApnaGuru Engine v2.5 • LMS
            </div>

          </aside>

          <!-- Dynamic Viewport Area (Isolated Scroll Region) -->
          <main id="app-shell-viewport" style="flex: 1; height: 100%; overflow-y: auto; box-sizing: border-box; position: relative;">
            <!-- Page component content will be mounted here -->
          </main>

        </div>

      </div>
    `;

    this.injectStylesIfNeeded();
    this.bindEvents();
  }

  private isRouteActive(path: string): boolean {
    if (path === '/lms-dashboard' || path === '/dashboard') {
      return this.currentPath === '/lms-dashboard' || this.currentPath === '/dashboard' || this.currentPath === '/' || this.currentPath === '';
    }
    return this.currentPath.startsWith(path);
  }

  private bindEvents(): void {
    // Brand click
    const brand = this.container.querySelector('#app-shell-brand');
    if (brand) brand.addEventListener('click', () => this.onNavigate('/lms-dashboard'));

    // Theme toggle
    const themeBtn = this.container.querySelector('#btn-toggle-theme');
    if (themeBtn) {
      themeBtn.addEventListener('click', () => {
        this.currentTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
        document.body.setAttribute('data-theme', this.currentTheme);
        localStorage.setItem('apnaguru_theme', this.currentTheme);
        this.render();
      });
    }

    // Logout
    const logoutBtn = this.container.querySelector('#btn-shell-logout');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        AuthService.getInstance().logout();
        this.onNavigate('/login');
      });
    }

    // Navigation buttons
    this.container.querySelectorAll<HTMLButtonElement>('.shell-nav-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const path = btn.getAttribute('data-path');
        if (path) {
          this.onNavigate(path);
        }
      });
    });
  }

  private injectStylesIfNeeded(): void {
    if (document.getElementById('app-shell-custom-styles')) return;

    const styleEl = document.createElement('style');
    styleEl.id = 'app-shell-custom-styles';
    styleEl.textContent = `
      .shell-nav-btn {
        width: 100%;
        background: transparent;
        border: none;
        color: var(--text-muted);
        padding: 10px 14px;
        border-radius: 10px;
        font-size: 13.5px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 12px;
        transition: all 0.2s ease;
        text-align: left;
        box-sizing: border-box;
      }
      .shell-nav-btn i {
        font-size: 16px;
        transition: transform 0.2s ease;
      }
      .shell-nav-btn:hover {
        background: var(--bg-surface-hover);
        color: var(--text-main);
      }
      .shell-nav-btn:hover i {
        transform: scale(1.15);
      }
      .shell-nav-btn.active {
        background: var(--brand-blue-glow);
        color: var(--brand-blue-primary);
        font-weight: 700;
        border: 1px solid rgba(0, 122, 255, 0.3);
      }
    `;
    document.head.appendChild(styleEl);
  }
}
